import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStableFunction } from '../../core/hooks/use-stable-function';
import { FullPageLoader } from '../../visual-components/full-page-loader/full-page-loader';
import isNil, { notNil } from '../../core/helpers/is-nil';
import { GridContainer, GridItem } from '../../visual-components/grid/grid';
import { ErrorSection } from '../../visual-components/error-section/error-section';
import { getLoggerInstance } from '../../core/monitoring/logger';
import { AuthContextProvider, AuthContextValue, useOptionalAuthContext } from './context';
import { useAccessTokenRetriever } from './hooks/use-access-token-retriever';

type Props = {
  children: React.ReactNode;
};

const logger = getLoggerInstance('auth/auth-guard');

const AuthGuardProvider: React.FC<Props> = ({ children }) => {
  const { isLoading, error, logout } = useAuth0();
  const { t } = useTranslation();

  const retrieveAccessToken = useAccessTokenRetriever();

  const stableLogout = useStableFunction(async () => {
    await logout({ logoutParams: { returnTo: window.location.origin } });
  });

  useEffect(() => {
    if (notNil(error)) {
      logger.warn('Auth0 Login failed', {
        exception: error,
      });
    }
  }, [error]);

  const contextValue: AuthContextValue = useMemo(
    () => ({
      retrieveAccessToken,
      logout: stableLogout,
    }),
    [retrieveAccessToken, stableLogout],
  );

  if (isLoading) {
    return <FullPageLoader />;
  }

  // this is displayed if the login fails on auth0 side (wrong configuration might be a cause)
  if (error) {
    return (
      <GridContainer>
        <GridItem>
          <ErrorSection error={error} title={t('auth.error.title')} />
        </GridItem>
      </GridContainer>
    );
  }

  return <AuthContextProvider value={contextValue}>{children}</AuthContextProvider>;
};

/**
 * Guarantees, that user is authenticated.
 * May be used multiple times within a component tree. Takes care of deduplicating the context.
 * @param children
 * @constructor
 */
export const AuthGuard: React.FC<Props> = ({ children }) => {
  const possibleAuthContext = useOptionalAuthContext();
  if (isNil(possibleAuthContext)) {
    return <AuthGuardProvider>{children}</AuthGuardProvider>;
  } else {
    return children;
  }
};
